import type { AdminDeductionRow } from '@billda/api';
import { summariseDeductions } from './summariseDeductions';

/** A grouping of deductions (by worker, site, etc.) for collapsible list rendering. */
export interface DeductionGroup {
  /** Human-readable group identifier (e.g. worker name or site name). */
  groupName: string;
  /** Deduction rows belonging to this group. */
  deductions: AdminDeductionRow[];
  /** Summed amount across all deductions in the group. */
  totalAmount: number;
  /** Number of deductions that affect the pot total. */
  count: number;
}

/**
 * Groups deductions by workerOrGangName, sorted alphabetically.
 * Each unique name becomes a collapsible group with its total.
 */
export function groupDeductionsByWorker(
  deductions: AdminDeductionRow[]
): DeductionGroup[] {
  const groupMap = new Map<string, AdminDeductionRow[]>();

  for (const deduction of deductions) {
    const name = deduction.workerOrGangName;
    const existing = groupMap.get(name);
    if (existing) {
      existing.push(deduction);
    } else {
      groupMap.set(name, [deduction]);
    }
  }

  return [...groupMap.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([groupName, groupDeductions]) => {
      const summary = summariseDeductions(groupDeductions);
      return {
        groupName,
        deductions: groupDeductions,
        totalAmount: summary.totalAmount,
        count: summary.count,
      };
    });
}
